import { Component, ReactNode } from 'react';
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary';
import { ConnectionError, ConnectionErrorType } from './ConnectionError';
import { healthCheckService, HealthStatus } from '@/services/healthCheck.service';

interface AppErrorBoundaryState {
  healthStatus: HealthStatus;
}

/**
 * Maps health status to connection error type
 */
function mapHealthStatusToErrorType(status: HealthStatus): ConnectionErrorType | null {
  switch (status) {
    case 'backend_down':
      return 'backend';
    case 'database_down':
      return 'database';
    case 'network_error':
      return 'network';
    case 'healthy':
    default:
      return null;
  }
}

/**
 * Custom fallback component for errors
 */
function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  // Determine error type from error message or default to backend
  let errorType: ConnectionErrorType = 'backend';

  if (error.message?.toLowerCase().includes('network')) {
    errorType = 'network';
  } else if (error.message?.toLowerCase().includes('database') || error.message?.toLowerCase().includes('timeout')) {
    errorType = 'database';
  }

  return (
    <ConnectionError
      type={errorType}
      onRetry={resetErrorBoundary}
      autoRetryInterval={60000}
    />
  );
}

/**
 * Wrapper component that monitors health and shows error UI
 */
class HealthMonitor extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
  private unsubscribe?: () => void;

  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = {
      healthStatus: healthCheckService.getLastStatus()
    };
  }

  componentDidMount() {
    // Start monitoring health
    healthCheckService.startMonitoring(60000); // Check every 1 minute

    // Subscribe to health changes
    this.unsubscribe = healthCheckService.subscribe((status) => {
      this.setState({ healthStatus: status });
    });
  }

  componentWillUnmount() {
    // Clean up
    this.unsubscribe?.();
    healthCheckService.stopMonitoring();
  }

  handleRetry = async () => {
    // Force a health check
    const result = await healthCheckService.checkHealth();

    // If healthy, force a re-render
    if (result.status === 'healthy') {
      this.setState({ healthStatus: 'healthy' });
    }
  };

  render() {
    const { healthStatus } = this.state;
    const errorType = mapHealthStatusToErrorType(healthStatus);

    // Show error UI if unhealthy
    if (errorType) {
      return (
        <ConnectionError
          type={errorType}
          onRetry={this.handleRetry}
          autoRetryInterval={60000}
        />
      );
    }

    // Render children if healthy
    return this.props.children;
  }
}

/**
 * Main error boundary that combines React error handling with health monitoring
 */
export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const handleError = (error: Error, errorInfo: { componentStack: string }) => {
    // Log error to console
    console.error('Application Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);

    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // Example:
    // errorTrackingService.captureException(error, {
    //   componentStack: errorInfo.componentStack
    // });
  };

  const handleReset = () => {
    // Force health check on reset
    healthCheckService.checkHealth();
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
      onReset={handleReset}
    >
      <HealthMonitor>{children}</HealthMonitor>
    </ReactErrorBoundary>
  );
}
